"use client"

import React, { type ReactNode } from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { ChevronRight, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { ModalScrollLockScope } from "@/components/ui/modal-scroll-lock"
import { BLADE_STACK_OFFSET, BLADE_WIDTHS, type BladeSize } from "@/lib/blade-stack"

export interface BladeProps {
  open: boolean
  onClose: () => void
  size?: BladeSize
  /** Where the blade sits in a stack: 0 = top; deeper blades peek out on the left and dim */
  depth?: number
  /** Labels of the blades beneath this one (oldest first); each is clickable to pop back */
  trail?: string[]
  onTrailClick?: (index: number) => void
  /** Small uppercase label above the title (the subject's kind, e.g. "Placement") */
  eyebrow?: ReactNode
  title: ReactNode
  description?: ReactNode
  /** Pills next to the title */
  badges?: ReactNode
  /** Icon/text actions in the header, left of the close button */
  actions?: ReactNode
  footer?: ReactNode
  /** Full-bleed body (no padding) for tables and tabs that manage their own spacing */
  bare?: boolean
  className?: string
  children: ReactNode
}

/**
 * Right-side slide-out in the Timesheet Hub's language: header with an eyebrow, a
 * Manrope title, pills and actions; a scrolling body; an optional footer rule for
 * actions. Built on Radix Dialog so focus is trapped per blade, ESC closes the top
 * one and the overlay click closes only the blade it belongs to. A blade beneath
 * another shifts left by a fixed offset so the stack reads as stacked cards.
 */
export function Blade({
  open,
  onClose,
  size = "md",
  depth = 0,
  trail = [],
  onTrailClick,
  eyebrow,
  title,
  description,
  badges,
  actions,
  footer,
  bare = false,
  className,
  children,
}: BladeProps) {
  return (
    <SheetPrimitive.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetPrimitive.Portal>
        <SheetPrimitive.Overlay className="fixed inset-0 z-50 bg-primary/25 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none" />
        <SheetPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            "fixed inset-y-0 right-0 z-50 flex h-full max-w-[96vw] flex-col bg-card text-card-foreground shadow-drawer",
            "transition-transform duration-300 ease-out data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right motion-reduce:animate-none motion-reduce:transition-none",
            className
          )}
          style={{
            width: BLADE_WIDTHS[size],
            transform: depth > 0 ? `translateX(-${depth * BLADE_STACK_OFFSET}px)` : undefined,
          }}
        >
          <ModalScrollLockScope>
            <header className="shrink-0 border-b border-card-border px-6 py-4">
              {(trail.length > 0 || eyebrow) && (
                <div className="th-section-label mb-1 flex flex-wrap items-center gap-1 text-[var(--th-label)]">
                  {trail.map((label, index) => (
                    <React.Fragment key={`${label}-${index}`}>
                      <button
                        type="button"
                        className="hover:text-foreground hover:underline"
                        onClick={() => onTrailClick?.(index)}
                      >
                        {label}
                      </button>
                      <ChevronRight className="h-3 w-3" />
                    </React.Fragment>
                  ))}
                  {eyebrow && <span className="text-[var(--th-section-label)]">{eyebrow}</span>}
                </div>
              )}
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <SheetPrimitive.Title className="font-display text-xl font-bold leading-tight tracking-tight text-foreground">
                    {title}
                  </SheetPrimitive.Title>
                  {description && (
                    <SheetPrimitive.Description className="mt-1 text-xs text-muted-foreground">{description}</SheetPrimitive.Description>
                  )}
                  {badges && <div className="mt-2 flex flex-wrap items-center gap-1.5">{badges}</div>}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {actions}
                  <SheetPrimitive.Close
                    className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="Close"
                  >
                    <X className="h-5 w-5" />
                  </SheetPrimitive.Close>
                </div>
              </div>
            </header>

            <div className={cn("min-h-0 flex-1 overflow-y-auto", !bare && "px-6 py-4")}>{children}</div>

            {footer && (
              <footer className="shrink-0 border-t border-[var(--th-menu-border)] px-6 py-3">
                <div className="flex items-center justify-between gap-3">{footer}</div>
              </footer>
            )}
          </ModalScrollLockScope>
        </SheetPrimitive.Content>
      </SheetPrimitive.Portal>
    </SheetPrimitive.Root>
  )
}

/** Hub section inside a blade: uppercase label, hairline below */
export function BladeSection({ label, children, className, action }: { label: ReactNode; children: ReactNode; className?: string; action?: ReactNode }) {
  return (
    <section className={cn("border-b border-[var(--th-menu-border)] py-4 last:border-b-0", className)}>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="th-section-label">{label}</h3>
        {action}
      </div>
      {children}
    </section>
  )
}

/** Hub detail field: mini label over a 13px value */
export function DetailField({ label, children, mono = false, full = false, title }: { label: ReactNode; children: ReactNode; mono?: boolean; full?: boolean; title?: string }) {
  return (
    <div className={cn("min-w-0", full && "col-span-full")} title={title}>
      <div className="th-field-label truncate">{label}</div>
      <div className={cn("mt-0.5 break-words text-[13px] text-foreground", mono && "font-mono text-xs")}>{children}</div>
    </div>
  )
}

/** Navy pill button used for a blade's primary footer action */
export function PillButton({ children, variant = "primary", className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "ghost" | "outline" }) {
  const styles = {
    primary: "bg-primary text-primary-foreground hover:bg-[#081229] dark:hover:bg-primary/90",
    ghost: "text-muted-foreground hover:text-foreground",
    outline: "border border-control-border bg-card text-foreground hover:border-[var(--th-control-border-hover)]",
  }
  return (
    <button
      type="button"
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill px-4 py-2 text-[13px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        styles[variant],
        className
      )}
      {...props}
    >
      {children}
    </button>
  )
}
